import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LicenseService } from '@/services/licenseService';
import { QUERY_KEYS } from '@/constants';
import { useAuthStore } from '@/store/authStore';
import { useInfiniteList } from '@/hooks/useInfiniteList';

export function useLicenseStatus() {
  const tenantId = useAuthStore((s) => s.tenant?.id || s.user?.tenantId);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  return useQuery({
    queryKey: QUERY_KEYS.licenseStatus,
    enabled: Boolean(tenantId) || isSuperAdmin(),
    queryFn: async () => {
      const res = await LicenseService.status();
      if (res.data) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setUser({ ...user, license: res.data });
      }
      return res.data;
    },
  });
}

export function useActivateLicense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => LicenseService.activate(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.licenseStatus });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.licenseInvoices });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.authMe });
    },
  });
}

export function useConfirmLicensePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      razorpayPaymentId: string;
      razorpayOrderId: string;
      razorpaySignature: string;
    }) => LicenseService.confirmPayment(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.licenseStatus });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.licenseInvoices });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.authMe });
    },
  });
}

export function useLicenseInvoices() {
  return useInfiniteList({
    queryKey: QUERY_KEYS.licenseInvoices,
    queryFn: (page) => LicenseService.listInvoices({ page, limit: 20 }),
  });
}
